import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCatalog } from '../../api/catalog';
import { fetchProductById, updateProduct, deleteProduct, setProductActive } from '../../api/admin/products.admin';
import { csrf } from '../../api/auth';
import FeatureFields from './FeatureFields';
import './ProductCreate.css';

function withPreview(file) {
  return Object.assign(file, {
    preview: URL.createObjectURL(file),
    mediaType: String(file.type || '').startsWith('video/') ? 'video' : 'image',
  });
}

export default function AdminProductEdit({ productId, embedded = false, onClose, onSaved } = {}) {
  const { id: routeId } = useParams();
  const id = productId || routeId;
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [cats, setCats] = useState([]);
  const [topSlug, setTopSlug] = useState('');
  const [subId, setSubId] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const [form, setForm] = useState({
    title: '', brand: '',
    sku: '', color: '', country: '',
    warrantyMonths: '',
    priceRetail: '', priceWholesale: '', costPrice: '',
    weightKg: '', lengthMm: '', widthMm: '', heightMm: '',
    shortDesc: '', fullDesc: '',
  });

  const [features, setFeatures] = useState([]);
  const [attributes, setAttributes] = useState({});
  const [existingImages, setExistingImages] = useState([]);
  const [imagesNew, setImagesNew] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const init = useCallback(async () => {
    const [catalog, prod] = await Promise.all([getCatalog(), fetchProductById(id)]);
    setCats(catalog || []);
    setTopSlug(prod.topCategorySlug);
    setSubId(String(prod.subcategoryId));
    const imgs = (prod.images || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setExistingImages(imgs);
    const pi = Math.max(0, imgs.findIndex((i) => i.isPrimary));
    setPrimaryIndex(pi === -1 ? 0 : pi);

    setForm({
      title: prod.title || '',
      brand: prod.brand || '',
      sku: prod.sku ?? '',
      color: prod.color ?? '',
      country: prod.country ?? '',
      warrantyMonths: prod.warrantyMonths ?? '',
      priceRetail: prod.priceRetail ?? '',
      priceWholesale: prod.priceWholesale ?? '',
      costPrice: prod.costPrice ?? '',
      weightKg: prod.weightKg ?? '',
      lengthMm: prod.dimensions?.lengthMm ?? '',
      widthMm: prod.dimensions?.widthMm ?? '',
      heightMm: prod.dimensions?.heightMm ?? '',
      shortDesc: prod.shortDesc ?? '',
      fullDesc: prod.fullDesc ?? '',
    });
    setAttributes(prod.attributes && typeof prod.attributes === 'object' ? { ...prod.attributes } : {});
    setIsActive(prod.isActive !== false);
  }, [id]);

  useEffect(() => { init(); }, [init]);

  const subcats = useMemo(() => {
    const c = cats.find((c) => c.slug === topSlug);
    return c?.subcategories || [];
  }, [cats, topSlug]);

  useEffect(() => {
    const sub = subcats.find((s) => String(s._id) === String(subId));
    const fs = sub?.features || [];
    setFeatures(fs);
    setAttributes((prev) => {
      const next = {};
      fs.forEach((f) => {
        if (prev[f.code] !== undefined && prev[f.code] !== null && prev[f.code] !== '') {
          next[f.code] = prev[f.code];
        } else if (typeof prev[f.code] === 'boolean') {
          next[f.code] = prev[f.code];
        } else {
          next[f.code] = '';
        }
      });
      return next;
    });
  }, [subId, subcats]);

  const onChange = (e) => {
    const { name, type, value } = e.target;
    const v = type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setForm((f) => ({ ...f, [name]: v }));
  };

  function moveExisting(from, to) {
    setExistingImages((prev) => {
      const arr = [...prev];
      const [x] = arr.splice(from, 1);
      arr.splice(to, 0, x);
      return arr.map((img, i) => ({ ...img, order: i }));
    });
    setPrimaryIndex((idx) => {
      if (from === idx) return to;
      if (from < idx && to >= idx) return idx - 1;
      if (from > idx && to <= idx) return idx + 1;
      return idx;
    });
  }

  function removeExisting(i) {
    setExistingImages((prev) => prev.filter((_, idx) => idx !== i));
    setPrimaryIndex((idx) => (i === idx ? 0 : (i < idx ? idx - 1 : idx)));
  }

  function removeNew(i) {
    setImagesNew((prev) => {
      const arr = [...prev];
      const [r] = arr.splice(i, 1);
      if (r?.preview) URL.revokeObjectURL(r.preview);
      return arr;
    });
  }

  function addFiles(fileList) {
    const files = Array.from(fileList || [])
      .filter((f) => f.type?.startsWith('image/') || f.type?.startsWith('video/'))
      .slice(0, 10)
      .map(withPreview);
    if (!files.length) return;
    setImagesNew((prev) => [...prev, ...files].slice(0, 10));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    setMsgOk(false);
    try {
      await csrf();
      const imagesKeep = existingImages.map((img) => img.url);
      await updateProduct(id, {
        ...form,
        topCategorySlug: topSlug,
        subcategoryId: subId,
        attributes,
        primaryIndex,
        imagesKeep,
        imagesNew,
      });
      setMsg('Сохранено');
      setMsgOk(true);
      await init();
      setImagesNew([]);
      onSaved?.();
    } catch (err) {
      setMsg(err?.response?.data?.error || 'Ошибка сохранения');
      setMsgOk(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={embedded ? 'pc-page pc-page--modal' : 'pc-page'}>
      {!embedded && <h1 className="pc-title admin-page-title">Редактирование товара</h1>}
      {!embedded && <p className="pc-lead">Изменения применяются после сохранения.</p>}
      {!isActive && (
        <div className="pc-msg">Товар в удалённых и скрыт с витрины. Можно восстановить.</div>
      )}
      {msg && <div className={`pc-msg ${msgOk ? 'is-ok' : 'is-err'}`}>{msg}</div>}

      <form className="pc-form" onSubmit={submit}>
        <section className="pc-card">
          <h3 className="pc-card__title">Основное</h3>
          <div className="grid two">
            <label className="pc-span-2">
              <span className="req">Название</span>
              <input name="title" value={form.title} onChange={onChange} required />
            </label>

            <label>
              Бренд
              <input name="brand" value={form.brand} onChange={onChange} />
            </label>

            <label>
              Артикул
              <input name="sku" value={form.sku} onChange={onChange} />
            </label>
            <label>
              Цвет
              <input name="color" value={form.color} onChange={onChange} placeholder="например Хром" />
            </label>
            <label>
              Страна
              <input name="country" value={form.country} onChange={onChange} placeholder="например Германия" />
            </label>

            <label>
              Гарантия, мес.
              <input name="warrantyMonths" type="number" min="0" step="1" value={form.warrantyMonths} onChange={onChange} />
            </label>

            <label>
              <span className="req">Категория</span>
              <select value={topSlug} onChange={(e) => { setTopSlug(e.target.value); setSubId(''); }}>
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name?.ru || c.slug}</option>)}
              </select>
            </label>
            <label>
              <span className="req">Подкатегория</span>
              <select value={subId} onChange={(e) => setSubId(e.target.value)} required>
                <option value="">— выберите —</option>
                {subcats.map((s) => <option key={s._id} value={s._id}>{s.name?.ru || s.slug}</option>)}
              </select>
            </label>

            <label className="pc-span-2">
              Полное описание
              <textarea name="fullDesc" value={form.fullDesc} onChange={onChange} />
            </label>
          </div>
        </section>

        <section className="pc-card">
          <h3 className="pc-card__title">Цены</h3>
          <p className="pc-help">Количество на склад меняйте в разделе «Склад».</p>
          <div className="grid three">
            <label>
              <span className="req">Розничная цена</span>
              <input name="priceRetail" type="number" min="0" step="1" value={form.priceRetail} onChange={onChange} required />
            </label>
            <label>
              <span className="req">Оптовая цена</span>
              <input name="priceWholesale" type="number" min="0" step="1" value={form.priceWholesale} onChange={onChange} required />
            </label>
            <label>
              Закупочная цена
              <input name="costPrice" type="number" min="0" step="0.01" value={form.costPrice} onChange={onChange} />
            </label>
          </div>
        </section>

        <section className="pc-card">
          <h3 className="pc-card__title">Габариты и вес</h3>
          <div className="grid four">
            <label>
              Вес, кг
              <input name="weightKg" type="number" min="0" step="0.01" value={form.weightKg} onChange={onChange} />
            </label>
            <label>
              Длина, мм
              <input name="lengthMm" type="number" min="0" step="1" value={form.lengthMm} onChange={onChange} />
            </label>
            <label>
              Ширина, мм
              <input name="widthMm" type="number" min="0" step="1" value={form.widthMm} onChange={onChange} />
            </label>
            <label>
              Высота, мм
              <input name="heightMm" type="number" min="0" step="1" value={form.heightMm} onChange={onChange} />
            </label>
          </div>
        </section>

        {subId && (
          <section className="pc-card">
            <h3 className="pc-card__title">Особенности</h3>
            <p className="pc-help">Все поля необязательны. В списке можно выбрать «Своё значение» — оно сохранится и появится в фильтрах.</p>
            <FeatureFields features={features} attributes={attributes} onChange={setAttributes} />
          </section>
        )}

        <section className="pc-card">
          <h3 className="pc-card__title">Фото и видео</h3>
          <p className="pc-help">Сначала существующие, ниже — новые. Видео сжимается на сервере.</p>

          {existingImages.length > 0 && (
            <div className="pc-images__list">
              {existingImages.map((img, i) => (
                <div key={img.url} className={`pc-thumb ${i === primaryIndex ? 'is-main' : ''}`}>
                  {img.type === 'video' ? (
                    <video src={img.url} muted playsInline preload="metadata" />
                  ) : (
                    <img src={img.url} alt="" />
                  )}
                  <div className="pc-thumb__actions">
                    <button type="button" onClick={() => setPrimaryIndex(i)} title="Сделать главной">⭐</button>
                    <button type="button" disabled={i === 0} onClick={() => moveExisting(i, i - 1)}>↑</button>
                    <button type="button" disabled={i === existingImages.length - 1} onClick={() => moveExisting(i, i + 1)}>↓</button>
                    <button type="button" onClick={() => removeExisting(i)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div
            className={`pc-drop ${dragOver ? 'is-drag' : ''}`}
            style={{ marginTop: 14 }}
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              multiple
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div className="pc-drop__title">Добавить фото или видео</div>
            <div className="pc-drop__hint">
              {imagesNew.length ? `Новых файлов: ${imagesNew.length}` : 'JPG, PNG, WEBP, MP4, WEBM, MOV'}
            </div>
          </div>

          {imagesNew.length > 0 && (
            <div className="pc-images__list">
              {imagesNew.map((f, i) => (
                <div key={i} className="pc-thumb">
                  {f.mediaType === 'video' ? (
                    <video src={f.preview} muted playsInline preload="metadata" />
                  ) : (
                    <img src={f.preview} alt="" />
                  )}
                  <div className="pc-thumb__actions">
                    <button type="button" onClick={() => removeNew(i)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="pc-actions">
          {isActive ? (
            <button
              type="button"
              className="btn-danger"
              onClick={async () => {
                if (!window.confirm('Переместить товар в удалённые? С витрины он исчезнет.')) return;
                try {
                  await deleteProduct(id);
                  alert('Товар перемещён в удалённые');
                  if (onClose) {
                    onSaved?.();
                    onClose();
                    return;
                  }
                  navigate('/admin/inventory');
                } catch (e) {
                  alert(e?.response?.data?.error || 'Не удалось удалить');
                }
              }}
            >
              Удалить
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              onClick={async () => {
                try {
                  await setProductActive(id, true);
                  setIsActive(true);
                  setMsg('Товар восстановлен');
                  setMsgOk(true);
                } catch (e) {
                  alert(e?.response?.data?.error || 'Не удалось восстановить');
                }
              }}
            >
              Восстановить
            </button>
          )}
          <button disabled={saving} type="submit" className="btn-primary">
            {saving ? 'Сохраняю…' : 'Сохранить изменения'}
          </button>
        </div>
      </form>
    </div>
  );
}
